import { Fragment } from "react";
import { Alert, Badge, Button, Spinner } from "react-bootstrap";
import type {
    IDwtProviderIssue,
    IDwtReceipt,
    IWasteMovementDwtSubmission,
} from "@/customTypings/wasteMovement/WasteMovement";
import {
    dwtDeadlineText,
    dwtLastCheckText,
    dwtLastCorrectionText,
    dwtModeBadge,
    dwtModeName,
    dwtRefreshButtonLabel,
    dwtStatusView,
    dwtSubmitButtonLabel,
    formatBroker,
    formatCarrier,
    formatDisposalOrRecovery,
    formatEwc,
    formatHazardous,
    formatPops,
    formatReceiptTime,
    formatTonnes,
    formatUkInstant,
    type DwtSubmitFeedback,
} from "../core/dwtSubmissionDisplay";
import type { WasteMovementDwtCorrectionState } from "../react/useWasteMovementDwtCorrection";
import type { WasteMovementDwtReadinessState } from "../react/useWasteMovementDwtReadiness";
import type { WasteMovementDwtSubmissionState } from "../react/useWasteMovementDwtSubmission";
import { DwtCorrectionSummary } from "./DwtCorrection";
import { DwtReadinessSummary } from "./DwtReadinessSummary";

export type { DwtSubmitFeedback } from "../core/dwtSubmissionDisplay";

/** Statuses after which another submission may be made (the server decides; this only shapes the display). */
const RESUBMITTABLE = new Set(["NotSubmitted", "Validated", "Invalid", "Rejected", "Cancelled", "Refused", "NotSent", "NoProviderRecord"]);

export interface DwtSubmissionPanelProps {
    readiness: WasteMovementDwtReadinessState | null;
    submission: WasteMovementDwtSubmissionState | null;
    submitting: boolean;
    /** S2C: a "Refresh DWT status" request is in progress. */
    refreshing?: boolean;
    feedback: DwtSubmitFeedback | null;
    /** The user may change waste movements (the server checks again). */
    canSubmit: boolean;
    onSubmit: () => void;
    /** S2C: asks the server to check the unresolved submission with the provider. */
    onRefresh?: () => void;
    /** S2D: what correcting the accepted filing would involve (loaded only while something is filed and the user may change movements). */
    correction?: WasteMovementDwtCorrectionState | null;
    /** S2D: a correction request is in progress. */
    correcting?: boolean;
    /** S2D: opens the correction dialog. */
    onCorrect?: () => void;
}

/**
 * S2B/S2C/S2D: DEFRA Digital Waste Tracking for a saved Waste Received movement - the server-configured mode, readiness, the
 * key values that will be submitted, and the persisted outcome (status, waste tracking ID, errors, warnings, deadline).
 * A submit button appears only when the server says a submission is permitted and the movement is ready - never for an
 * accepted filing; a "Refresh DWT status" button only when the server says there is an unresolved submission it can check;
 * and "Correct DEFRA filing" only when the saved movement differs from an accepted filing and the server says the
 * correction can be sent.
 */
export function DwtSubmissionPanel({
    readiness,
    submission,
    submitting,
    refreshing = false,
    feedback,
    canSubmit,
    onSubmit,
    onRefresh,
    correction = null,
    correcting = false,
    onCorrect,
}: DwtSubmissionPanelProps) {
    const loaded = submission?.status === "loaded" ? submission.submission : null;
    const readinessLoaded = readiness?.status === "loaded" ? readiness.readiness : null;
    const receipt = readinessLoaded?.receipt ?? null;
    const badge = dwtModeBadge(loaded?.mode);
    const historyAllowsSubmit = !!loaded && loaded.configured && loaded.submissionPermitted;
    const showSubmit = historyAllowsSubmit && !!readinessLoaded?.isReady && !!receipt && canSubmit;
    const showRefresh = !!loaded && loaded.refreshAvailable && canSubmit && !!onRefresh;

    return (
        <div className="waste-movement-form-panel mb-3" data-testid="dwt-submission-panel">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <div className="fw-semibold">DEFRA Digital Waste Tracking</div>
                {loaded && (
                    <Badge bg={badge.variant} data-testid="dwt-mode-badge" title={badge.description}>
                        {badge.label}
                    </Badge>
                )}
                {loaded?.mode && <span className="small text-muted">{badge.description}</span>}
            </div>

            {feedback && (
                <Alert variant={feedback.variant} className="py-2 mb-2" data-testid="dwt-submit-feedback">
                    {feedback.message}
                </Alert>
            )}

            {submission?.status === "loading" && <div className="text-muted small mb-2">Loading the DEFRA submission status…</div>}
            {submission?.status === "error" && (
                <div className="text-muted small mb-2" data-testid="dwt-submission-error">
                    The DEFRA submission status could not be loaded. Reload the page to try again.
                </div>
            )}

            {loaded && !loaded.configured && (
                <Alert variant="secondary" className="py-2 mb-2" data-testid="dwt-not-configured">
                    {loaded.configurationProblem ?? "DEFRA submission is not configured on this server."}
                </Alert>
            )}

            {loaded && (loaded.hasRecord || loaded.inFlight) && <DwtSubmissionStatus submission={loaded} />}

            {loaded && !loaded.inFlight && (
                <DwtCorrectionSummary
                    correction={correction}
                    canCorrect={canSubmit}
                    correcting={correcting}
                    busy={correcting || refreshing || submitting}
                    onCorrect={onCorrect}
                />
            )}

            {showRefresh && loaded && (
                <div className="mb-2">
                    <Button variant="outline-primary" size="sm" onClick={onRefresh} disabled={refreshing || submitting || correcting} data-testid="dwt-refresh-button">
                        {refreshing ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" aria-hidden="true" />
                                Checking with {loaded.provider}…
                            </>
                        ) : (
                            dwtRefreshButtonLabel(loaded)
                        )}
                    </Button>
                </div>
            )}

            {loaded && loaded.configured && !loaded.submissionPermitted && loaded.blockedReason && RESUBMITTABLE.has(loaded.status) && (
                <div className="small mb-2" data-testid="dwt-blocked-reason">
                    {loaded.blockedReason}
                </div>
            )}

            {readiness && (!loaded || !loaded.configured || historyAllowsSubmit) && <DwtReadinessSummary state={readiness} />}

            {showSubmit && receipt && loaded && (
                <>
                    <div className="small fw-semibold mb-1">What will be submitted</div>
                    <DwtReceiptSummary receipt={receipt} />
                    <Button
                        variant={loaded.mode === "live" ? "danger" : loaded.mode === "test" ? "warning" : "primary"}
                        onClick={onSubmit}
                        disabled={submitting}
                        data-testid="dwt-submit-button"
                    >
                        {submitting ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" aria-hidden="true" />
                                Sending to {loaded.provider}…
                            </>
                        ) : (
                            dwtSubmitButtonLabel(loaded.mode)
                        )}
                    </Button>
                </>
            )}
        </div>
    );
}

/** The persisted outcome of the latest attempt. */
export function DwtSubmissionStatus({ submission }: { submission: IWasteMovementDwtSubmission }) {
    const view = dwtStatusView(submission);
    const deadline = dwtDeadlineText(submission);
    const lastCheck = dwtLastCheckText(submission);
    const otherModes = submission.modeOutcomes.filter((o) => o.mode !== submission.statusMode);

    return (
        <div className="mb-2" data-testid="dwt-submission-status" data-status={submission.status}>
            <Alert variant={view.variant} className="py-2 mb-2">
                <div className="fw-semibold" data-testid="dwt-status-title">
                    {view.title}
                </div>
                {view.explanation && <div className="small">{view.explanation}</div>}
                {submission.wasteTrackingId && (
                    <div className="mt-2" data-testid="dwt-waste-tracking-id">
                        <span className="small text-muted d-block">Waste tracking ID</span>
                        <span className="fs-4 fw-bold font-monospace">{submission.wasteTrackingId}</span>
                    </div>
                )}
                {submission.status === "Accepted" && !submission.wasteTrackingId && (
                    <div className="small mt-1">No waste tracking ID was returned; check the status with {submission.provider}.</div>
                )}
            </Alert>
            <dl className="row small mb-2">
                {submission.reference && (
                    <>
                        <dt className="col-sm-4">DWT reference</dt>
                        <dd className="col-sm-8 font-monospace" data-testid="dwt-reference">
                            {submission.reference}
                        </dd>
                    </>
                )}
                {submission.statusMode && (
                    <>
                        <dt className="col-sm-4">Mode</dt>
                        <dd className="col-sm-8">{dwtModeName(submission.statusMode)}</dd>
                    </>
                )}
                {submission.submittedAtUtc && (
                    <>
                        <dt className="col-sm-4">Submitted</dt>
                        <dd className="col-sm-8">{formatUkInstant(submission.submittedAtUtc)}</dd>
                    </>
                )}
                {submission.lastAttemptAtUtc && (
                    <>
                        <dt className="col-sm-4">Last attempt</dt>
                        <dd className="col-sm-8">
                            {formatUkInstant(submission.lastAttemptAtUtc)}
                            {submission.attemptCount > 1 ? ` (attempt ${submission.attemptCount})` : ""}
                        </dd>
                    </>
                )}
                {submission.providerMessage && (
                    <>
                        <dt className="col-sm-4">{submission.provider} says</dt>
                        <dd className="col-sm-8" data-testid="dwt-provider-message">
                            {submission.providerMessage}
                        </dd>
                    </>
                )}
                {(submission.providerErrorCode || submission.providerReason) && (
                    <>
                        <dt className="col-sm-4">Code</dt>
                        <dd className="col-sm-8 font-monospace">{submission.providerErrorCode ?? submission.providerReason}</dd>
                    </>
                )}
                {submission.transportError && (
                    <>
                        <dt className="col-sm-4">Connection</dt>
                        <dd className="col-sm-8" data-testid="dwt-transport-error">
                            {submission.transportError}
                        </dd>
                    </>
                )}
            </dl>
            {deadline && (
                <Alert variant={deadline.variant} className="py-1 px-2 small mb-2" data-testid="dwt-deadline">
                    {deadline.text}
                </Alert>
            )}
            {lastCheck && (
                <div className={`small mb-2 ${lastCheck.settled ? "text-muted" : ""}`} data-testid="dwt-last-check" data-settled={lastCheck.settled}>
                    {lastCheck.text}
                </div>
            )}
            <DwtIssueList testId="dwt-errors" title="Errors" issues={submission.errors} />
            <DwtIssueList testId="dwt-warnings" title="Warnings" issues={submission.warnings} />
            {submission.lastCorrection && (
                <div className="small mb-2" data-testid="dwt-last-correction" data-status={submission.lastCorrection.status}>
                    <div>{dwtLastCorrectionText(submission.lastCorrection)}</div>
                    {submission.lastCorrection.reason && <div className="text-muted">Reason: {submission.lastCorrection.reason}</div>}
                    {submission.lastCorrection.providerMessage && submission.lastCorrection.status === "CorrectionRefused" && (
                        <div className="text-muted">
                            {submission.provider} says: {submission.lastCorrection.providerMessage}
                        </div>
                    )}
                </div>
            )}
            {submission.lastCorrection?.status === "CorrectionRefused" && (
                <DwtIssueList testId="dwt-correction-errors" title="Why the correction was refused" issues={submission.lastCorrection.errors} />
            )}
            {submission.changedSinceLastAttempt && (
                <div className="small text-muted mb-2" data-testid="dwt-changed">
                    {submission.filingMode
                        ? "The movement has changed since it was last sent to DEFRA. The filing must be corrected to match; a user who may change waste movements can correct it."
                        : "The movement has changed since the last attempt."}
                </div>
            )}
            {otherModes.length > 0 && (
                <div className="small text-muted mb-2" data-testid="dwt-other-modes">
                    {otherModes.map((o) => (
                        <div key={`${o.mode}-${o.attemptNumber}`}>
                            Earlier in {dwtModeName(o.mode)}: {o.status}
                            {o.wasteTrackingId ? ` (waste tracking ID ${o.wasteTrackingId})` : ""}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function DwtIssueList({ testId, title, issues }: { testId: string; title: string; issues: IDwtProviderIssue[] }) {
    if (issues.length === 0) return null;
    return (
        <div className="small mb-2" data-testid={testId}>
            <div className="fw-semibold">{title}</div>
            <ul className="mb-0">
                {issues.map((issue, index) => (
                    <li key={`${issue.key ?? ""}-${index}`}>
                        {issue.key && <span className="font-monospace">{issue.key}: </span>}
                        {issue.message ?? issue.errorType ?? "No details given."}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** The canonical values the operator confirms, exactly as the server judged them. */
export function DwtReceiptSummary({ receipt }: { receipt: IDwtReceipt }) {
    const item = receipt.wasteItem;
    const broker = formatBroker(receipt);
    const rows: [string, string][] = [
        ["Received", formatReceiptTime(receipt.dateTimeReceived)],
        ["EWC", formatEwc(item.ewcCode, item.ewcClassifiedHazardous)],
        ["Description", item.description ?? "Not recorded"],
        ["Weight", formatTonnes(item.weightTonnes, item.weightIsEstimate)],
        ["Container", `${item.containerType ?? "Not recorded"} × ${item.numberOfContainers ?? "?"} (${item.physicalForm ?? "form not recorded"})`],
        ["Hazardous", formatHazardous(receipt)],
        ["POPs", formatPops(receipt)],
        ["Disposal / recovery", formatDisposalOrRecovery(receipt)],
        ["Carrier", formatCarrier(receipt)],
    ];
    if (item.containsHazardous) {
        rows.push(["Consignment", receipt.hazardousWasteConsignmentCode ?? (receipt.reasonForNoConsignmentCode ? `None: ${receipt.reasonForNoConsignmentCode}` : "Not recorded")]);
    }
    if (broker) rows.push(["Broker / dealer", broker]);
    if (receipt.ticketReference) rows.push(["Ticket reference", receipt.ticketReference]);

    return (
        <dl className="row small mb-2" data-testid="dwt-receipt-summary">
            {rows.map(([label, value]) => (
                <Fragment key={label}>
                    <dt className="col-sm-4">{label}</dt>
                    <dd className="col-sm-8" data-field={label}>
                        {value}
                    </dd>
                </Fragment>
            ))}
        </dl>
    );
}

/** The confirmation shown immediately before submitting; LIVE is unmistakable. */
export function DwtSubmitConfirmation({
    mode,
    receipt,
    reference,
    provider,
}: {
    mode: string | null | undefined;
    receipt: IDwtReceipt;
    reference: string | null | undefined;
    provider: string;
}) {
    return (
        <div data-testid="dwt-submit-confirmation" data-mode={mode ?? ""}>
            {mode === "live" && (
                <Alert variant="danger" className="mb-3" data-testid="dwt-confirm-live">
                    <div className="fw-bold fs-5">LIVE - this is a real regulatory filing with DEFRA</div>
                    <div>DEFRA records it against this site's permit. Changing it afterwards needs a correction to that filing, with a reason shown to the regulator (and a second person's approval for weight, EWC or disposal/recovery changes).</div>
                </Alert>
            )}
            {mode === "test" && (
                <Alert variant="warning" className="mb-3" data-testid="dwt-confirm-test">
                    <div className="fw-semibold">TEST - DEFRA's test service</div>
                    <div>This is not a real filing.</div>
                </Alert>
            )}
            {mode === "dry" && (
                <Alert variant="secondary" className="mb-3" data-testid="dwt-confirm-dry">
                    <div className="fw-semibold">DRY RUN - checks only</div>
                    <div>{provider} checks the movement and builds the DEFRA receipt. Nothing is sent to DEFRA.</div>
                </Alert>
            )}
            <DwtReceiptSummary receipt={receipt} />
            <div className="small text-muted">
                DWT reference: {reference ? <span className="font-monospace">{reference}</span> : "created with the first submission"}. It stays the same for every
                later attempt.
            </div>
        </div>
    );
}
